import { useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, EmptyState, Stat } from "@/components/common";
import { Briefcase, Plus, Trash2, Building2, Clock, Wallet, Users, UserCog, ChevronDown, CalendarDays, AlertTriangle } from "lucide-react";
import { fDate } from "@/lib/format";
import { toast } from "sonner";
import PlaceringsKalender from "@/components/PlaceringsKalender";

const STATUS = { planned: "Planerad", active: "Aktiv", ended: "Avslutad" };
const money = (n) => `${Number(n || 0).toLocaleString("sv-SE")} kr`;

function ClientForm({ onDone }) {
  const [f, setF] = useState({ name: "", contact_name: "", contact_email: "", contact_phone: "", ref: "" });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Namn krävs");
    try { await api.post("/placements/clients", f); toast.success("Kund sparad"); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="grid gap-3 p-4 sm:grid-cols-2" data-testid="client-form">
      <div><Label>Kundföretag</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="client-name" /></div>
      <div><Label>Kontaktperson</Label><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="client-contact" /></div>
      <div><Label>E-post</Label><Input value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} /></div>
      <div><Label>Telefon</Label><Input value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></div>
      <div className="sm:col-span-2 flex justify-end"><Button onClick={save} data-testid="client-save"><Plus className="mr-1 h-4 w-4" />Spara kund</Button></div>
    </Card>
  );
}

function PlacementForm({ clients, employees, placements, onDone }) {
  const [f, setF] = useState({ client_id: "", employee_id: "", role_title: "", start_date: "", end_date: "", hourly_rate: "", status: "planned" });
  const [ackd, setAckd] = useState(false);

  const overlaps = (() => {
    if (!f.employee_id || !f.start_date) return [];
    const ns = new Date(`${f.start_date}T00:00:00`);
    const ne = f.end_date ? new Date(`${f.end_date}T00:00:00`) : new Date("2999-12-31");
    return (placements || []).filter((p) => p.employee_id === f.employee_id && p.status !== "ended" && p.start_date)
      .filter((p) => {
        const ps = new Date(`${p.start_date}T00:00:00`);
        const pe = p.end_date ? new Date(`${p.end_date}T00:00:00`) : new Date("2999-12-31");
        return ns < pe && ps < ne;
      });
  })();

  const change = (patch) => { setF({ ...f, ...patch }); setAckd(false); };

  const save = async () => {
    if (!f.client_id || !f.employee_id) return toast.error("Välj kund och konsult");
    if (overlaps.length && !ackd) {
      setAckd(true);
      return toast.warning("Dubbelbokning – konsulten har redan en placering i perioden. Klicka \"Spara ändå\" för att bekräfta.");
    }
    try { await api.post("/placements/placements", { ...f, hourly_rate: Number(f.hourly_rate) || 0 }); toast.success("Placering skapad"); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="grid gap-3 p-4 sm:grid-cols-2" data-testid="placement-form">
      <div><Label>Kund</Label>
        <Select value={f.client_id} onValueChange={(v) => change({ client_id: v })}>
          <SelectTrigger data-testid="pl-client"><SelectValue placeholder="Välj kund" /></SelectTrigger>
          <SelectContent>{(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><Label>Konsult</Label>
        <Select value={f.employee_id} onValueChange={(v) => change({ employee_id: v })}>
          <SelectTrigger data-testid="pl-employee"><SelectValue placeholder="Välj konsult" /></SelectTrigger>
          <SelectContent>{(employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{`${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><Label>Roll/titel</Label><Input value={f.role_title} onChange={(e) => change({ role_title: e.target.value })} data-testid="pl-role" /></div>
      <div><Label>Timpris (kr, fakturerbart)</Label><Input type="number" value={f.hourly_rate} onChange={(e) => change({ hourly_rate: e.target.value })} data-testid="pl-rate" /></div>
      <div><Label>Start</Label><Input type="date" value={f.start_date} onChange={(e) => change({ start_date: e.target.value })} data-testid="pl-start" /></div>
      <div><Label>Slut (valfritt)</Label><Input type="date" value={f.end_date} onChange={(e) => change({ end_date: e.target.value })} /></div>
      <div><Label>Status</Label>
        <Select value={f.status} onValueChange={(v) => change({ status: v })}>
          <SelectTrigger data-testid="pl-status"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select></div>
      {overlaps.length > 0 && (
        <div className="sm:col-span-2 flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900" data-testid="pl-conflict-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <div className="font-medium">Dubbelbokning – konsulten är redan placerad i perioden:</div>
            <ul className="mt-1 list-disc pl-4">
              {overlaps.map((p) => <li key={p.id}>{p.client_name} · {p.start_date ? fDate(p.start_date) : "—"}{p.end_date ? ` – ${fDate(p.end_date)}` : " – pågående"}</li>)}
            </ul>
          </div>
        </div>
      )}
      <div className="sm:col-span-2 flex items-end justify-end">
        <Button onClick={save} variant={overlaps.length && ackd ? "destructive" : "default"} data-testid="placement-save">
          <Plus className="mr-1 h-4 w-4" />{overlaps.length && ackd ? "Spara ändå" : "Skapa placering"}
        </Button>
      </div>
    </Card>
  );
}

function TimeDialog({ placement, onClose, onChanged }) {
  const { data: entries, reload } = useApi(placement ? `/placements/placements/${placement.id}/time` : null, [placement?.id]);
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), hours: "", note: "" });
  const add = async () => {
    if (!f.hours) return toast.error("Ange timmar");
    try { await api.post(`/placements/placements/${placement.id}/time`, { ...f, hours: Number(f.hours) }); setF({ ...f, hours: "", note: "" }); reload(); onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/placements/time/${id}`); reload(); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const total = (entries || []).reduce((s, e) => s + Number(e.hours || 0), 0);
  return (
    <Dialog open={!!placement} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="time-dialog">
        <DialogHeader><DialogTitle>Tidrapport – {placement?.employee_name}</DialogTitle>
          <DialogDescription>{placement?.role_title} · {money(placement?.hourly_rate)}/tim · summa {total} tim = {money(total * (placement?.hourly_rate || 0))}</DialogDescription>
        </DialogHeader>
        <div className="flex items-end gap-2">
          <div><Label>Datum</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="time-date" /></div>
          <div className="w-24"><Label>Timmar</Label><Input type="number" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="time-hours" /></div>
          <div className="flex-1"><Label>Notering</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
          <Button onClick={add} data-testid="time-add"><Plus className="h-4 w-4" /></Button>
        </div>
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {(entries || []).map((e) => (
            <div key={e.id} className="flex items-center gap-2 rounded border p-2 text-sm" data-testid={`time-${e.id}`}>
              <span>{fDate(e.date)}</span><Badge variant="secondary">{e.hours} tim</Badge><span className="flex-1 truncate text-muted-foreground">{e.note}</span>
              <button onClick={() => del(e.id)}><Trash2 className="h-4 w-4 text-red-500" /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Managers() {
  const { data, reload } = useApi("/placements/managers");
  const toggle = async (u) => { try { await api.post("/placements/managers", { user_id: u.id, enabled: !u.is_manager }); reload(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Card className="p-4" data-testid="managers-card">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><UserCog className="h-4 w-4 text-accent" />Ansvariga (utvald person)</div>
      <div className="space-y-1">
        {(data || []).map((u) => (
          <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded border p-2 text-sm" data-testid={`mgr-${u.id}`}>
            <input type="checkbox" checked={u.is_manager} onChange={() => toggle(u)} />
            <span className="flex-1">{u.name}</span><Badge variant="outline">{u.role}</Badge>
          </label>
        ))}
      </div>
    </Card>
  );
}

export default function Placeringar() {
  const { user } = useAuth();
  const isAdmin = ["owner", "hr_admin", "master_admin"].includes(user?.role);
  const canSeePeople = (user?.access_level ?? 1) >= 3;
  const { data: summary, reload: reloadSum } = useApi("/placements/summary");
  const { data: clients, reload: reloadClients } = useApi("/placements/clients");
  const { data: employees } = useApi(canSeePeople ? "/employees" : null);
  const { data: availability } = useApi("/placements/availability");
  const [tab, setTab] = useState("placements");
  const [showClient, setShowClient] = useState(false);
  const [showPlacement, setShowPlacement] = useState(false);
  const [timeOf, setTimeOf] = useState(null);
  const totals = summary?.totals || { hours: 0, amount: 0, placements: 0 };
  const reloadAll = () => { reloadSum(); reloadClients(); };

  const setStatus = async (p, status) => { try { await api.patch(`/placements/placements/${p.id}`, { status }); reloadSum(); } catch (e) { toast.error(apiErr(e)); } };
  const reschedule = async (p, patch) => { try { await api.patch(`/placements/placements/${p.id}`, patch); toast.success("Placering flyttad"); reloadSum(); } catch (e) { toast.error(apiErr(e)); } };
  const delPlacement = async (p) => { if (!window.confirm("Ta bort placeringen?")) return; try { await api.delete(`/placements/placements/${p.id}`); reloadSum(); } catch (e) { toast.error(apiErr(e)); } };

  const allPlacements = (summary?.clients || []).flatMap((c) => (c.placements || []).map((p) => ({ ...p, client_name: c.name })));

  return (
    <div className={`mx-auto ${tab === "calendar" ? "max-w-6xl" : "max-w-4xl"}`}>
      <PageHeader title="Kund & placering" icon={Briefcase} subtitle="Hantera kundföretag, konsultplaceringar och fakturerbar tid." />

      <div className="mb-4 grid grid-cols-3 gap-3">
        <Stat label="Placeringar" value={totals.placements} icon={Briefcase} tone="accent" />
        <Stat label="Fakturerbara timmar" value={totals.hours} icon={Clock} tone="ok" />
        <Stat label="Fakturerbart belopp" value={money(totals.amount)} icon={Wallet} tone="warn" />
      </div>

      <div className="mb-4 flex gap-2">
        <Button variant={tab === "placements" ? "default" : "outline"} size="sm" onClick={() => setTab("placements")} data-testid="tab-placements">Placeringar</Button>
        <Button variant={tab === "calendar" ? "default" : "outline"} size="sm" onClick={() => setTab("calendar")} data-testid="tab-calendar"><CalendarDays className="mr-1 h-4 w-4" />Kalender</Button>
        <Button variant={tab === "clients" ? "default" : "outline"} size="sm" onClick={() => setTab("clients")} data-testid="tab-clients">Kunder</Button>
        {isAdmin && <Button variant={tab === "managers" ? "default" : "outline"} size="sm" onClick={() => setTab("managers")} data-testid="tab-managers">Ansvariga</Button>}
      </div>

      {tab === "clients" && (
        <div className="space-y-3">
          <div className="flex justify-end"><Button onClick={() => setShowClient((v) => !v)} data-testid="new-client-btn"><Plus className="mr-1 h-4 w-4" />Ny kund</Button></div>
          {showClient && <ClientForm onDone={() => { setShowClient(false); reloadClients(); }} />}
          {(clients || []).length === 0 ? <EmptyState icon={Building2} title="Inga kunder" text="Lägg till ett kundföretag för att börja." /> :
            (clients || []).map((c) => (
              <Card key={c.id} className="flex items-center gap-3 p-4" data-testid={`client-${c.id}`}>
                <Building2 className="h-4 w-4 text-accent" />
                <div className="flex-1"><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.contact_name} {c.contact_email && `· ${c.contact_email}`}</div></div>
                <button onClick={async () => { if (window.confirm("Ta bort kund och dess placeringar?")) { await api.delete(`/placements/clients/${c.id}`); reloadAll(); } }}><Trash2 className="h-4 w-4 text-red-500" /></button>
              </Card>
            ))}
        </div>
      )}

      {tab === "placements" && (
        <div className="space-y-3">
          <div className="flex justify-end"><Button onClick={() => setShowPlacement((v) => !v)} data-testid="new-placement-btn"><Plus className="mr-1 h-4 w-4" />Ny placering</Button></div>
          {showPlacement && <PlacementForm clients={clients} employees={employees} placements={allPlacements} onDone={() => { setShowPlacement(false); reloadSum(); }} />}
          {allPlacements.length === 0 ? <EmptyState icon={Briefcase} title="Inga placeringar" text="Koppla en konsult till en kund för att skapa en placering." /> :
            allPlacements.map((p) => (
              <Card key={p.id} className="flex flex-wrap items-center gap-2 p-4" data-testid={`placement-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{p.employee_name} <span className="text-muted-foreground">→ {p.client_name}</span></div>
                  <div className="text-xs text-muted-foreground">{p.role_title} · {money(p.hourly_rate)}/tim · {p.start_date ? fDate(p.start_date) : "—"}{p.end_date ? ` – ${fDate(p.end_date)}` : ""}</div>
                </div>
                <Badge variant="secondary">{p.hours} tim · {money(p.amount)}</Badge>
                <Select value={p.status} onValueChange={(v) => setStatus(p, v)}>
                  <SelectTrigger className="w-32" data-testid={`pl-status-${p.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => setTimeOf(p)} data-testid={`pl-time-${p.id}`}><Clock className="mr-1 h-4 w-4" />Tid</Button>
                <button onClick={() => delPlacement(p)}><Trash2 className="h-4 w-4 text-red-500" /></button>
              </Card>
            ))}
        </div>
      )}

      {tab === "calendar" && <PlaceringsKalender placements={allPlacements} availability={availability || []} onReschedule={reschedule} />}

      {tab === "managers" && isAdmin && <Managers />}

      {timeOf && <TimeDialog placement={timeOf} onClose={() => setTimeOf(null)} onChanged={reloadSum} />}
    </div>
  );
}
